import * as _ from 'underscore'

import { $TIMEOUT_SERVICE_NAME } from '../system'
import { OpLocation, OpViewColumn, OpDevice, OpMeter, OpDeviceType } from "../../models/core"
import { IOpObject } from "../../models/util/interfaces"
import { SourcePopover } from "../../modules/common/select-box-popup/select-box-popup.directive"
import { Enums } from "../../models/common"
import { GetViewColumnOptions, OpView } from "../../models/core/op-view"
import { LocalizationHelper, ToastHelper, CommonHelper, Dictionary, EventsNames } from "../../helpers"
import { LocationFormValuesModel, DeviceMeterModel } from "../../modules/api/api.models"
import { UserSessionService, USER_SESSION_SERVICE_NAME } from '../user-session'

// #region Consts

export const LOCATION_EQUIPMENT_SERVICE_NAME: string = 'LocationEquipmentService'
export const LOCATION_EQUIPMENT_SERVICE_PROVIDER_NAME: string = 'LocationEquipmentServiceProvider'

// #endregion

// #region Types

export type InstalledEquipmentLegacyOptions = {
    editId: number
    scope: any

    addedRows: any[],
    removedRows: any[],
    inGrid: any[],

    locationView: OpView,
    deviceView: OpView,
    location: OpLocation,

    deviceTypesDict: Dictionary<OpDeviceType>

    saveLocationDelegate: (toSave: OpLocation) => JQueryPromise<OpLocation>,
    locationSavedDelegate: (toSave: OpLocation) => boolean,
    saveFailedDelegate: () => boolean,
    saveAlwaysDelegate: () => void

    setConfirmationSourceDelegates: (onConfirmed: () => void, onCanceled: () => void) => void
}

// #endregion

// #region LocationEquipmentService

export class LocationEquipmentService {
    // #region Consts


    // #endregion

    // #region Ctor

    constructor(
        private $timeout: ng.ITimeoutService,
        private userSession: UserSessionService
    ) {
    }

    // #endregion

    // #region Public Methods

    // #endregion

    // #region Backward compatibility

    public translateLocationModelToLocationEquipmentChanges(locationModel: LocationFormValuesModel): OpLocation {
        const toSave: OpLocation = new OpLocation(null)
        toSave.currentDevices = []
        toSave.currentMeters = []

        if (locationModel.id > 0) {
            toSave.idLocation = locationModel.id
            toSave.opState = Enums.OpChangeState.Modified
        }

        const createChangesList: (factory: () => IOpObject, objIds: number[], addedIds: number[], removedIds: number[]) => IOpObject[]
            = (factory: () => IOpObject, objIds: number[], addedIds: number[], removedIds: number[]): IOpObject[] => {
                return objIds
                    .filter((id: number) => removedIds.every((rId: number) => rId !== id))
                    .map((id: number) => {
                        const obj: IOpObject = factory()
                        obj.idObject = id
                        obj.opState = Enums.OpChangeState.Loaded
                        return obj
                    })
                    .concat(addedIds.map((id: number) => {
                        const obj: IOpObject = factory()
                        obj.idObject = id
                        obj.opState = Enums.OpChangeState.New
                        return obj
                    }))
                    .concat(removedIds.map((id: number) => {
                        const obj: IOpObject = factory()
                        obj.idObject = id
                        obj.opState = Enums.OpChangeState.Delete
                        return obj
                    }))
            }

        if (locationModel.devices != null && locationModel.removedDevices != null && locationModel.addedDevices != null) {
            toSave.currentDevices = createChangesList(
                () => new OpDevice(null),
                locationModel.devices,
                locationModel.addedDevices,
                locationModel.removedDevices
            ).map((obj: IOpObject) => obj as OpDevice)
        }

        if (locationModel.meters != null && locationModel.removedMeters != null && locationModel.addedMeters != null) {
            toSave.currentMeters = createChangesList(
                () => new OpMeter(null),
                locationModel.meters,
                locationModel.addedMeters,
                locationModel.removedMeters
            ).map((obj: IOpObject) => obj as OpMeter)
        }

        // #region Usuwanie urządzenia/metera wraz z przeniesieniem do magazynu

        if (locationModel.locationToAssign != null) {

            // #region Usuwanie urządzenia

            if (locationModel.removeWithDevices == null) {
                toSave.currentDevices.forEach((device: OpDevice) => {
                    if (device.opState == Enums.OpChangeState.Delete) {
                        const location: OpLocation = new OpLocation(null)
                        location.idLocation = locationModel.locationToAssign
                        location.opState = Enums.OpChangeState.Loaded

                        device.currentLocations.push(location)
                    }
                })

                if (locationModel.removeWithMeters) {
                    toSave.currentMeters.forEach((meter: OpMeter) => {
                        if (meter.opState == Enums.OpChangeState.Delete) {
                            const location: OpLocation = new OpLocation(null)
                            location.idLocation = locationModel.locationToAssign
                            location.opState = Enums.OpChangeState.Loaded

                            meter.currentLocations.push(location)

                            meter.opState = Enums.OpChangeState.Loaded
                        }
                    })
                }
            }

            // #endregion

            // #region Usuwanie metera

            if (locationModel.removeWithMeters == null) {
                toSave.currentMeters.forEach((meter: OpMeter) => {
                    if (meter.opState == Enums.OpChangeState.Delete) {
                        const location: OpLocation = new OpLocation(null)
                        location.idLocation = locationModel.locationToAssign
                        location.opState = Enums.OpChangeState.Loaded

                        meter.currentLocations.push(location)
                    }
                })

                if (locationModel.removeWithDevices) {
                    toSave.currentDevices.forEach((device: OpDevice) => {
                        if (device.opState == Enums.OpChangeState.Delete) {
                            const location: OpLocation = new OpLocation(null)
                            location.idLocation = locationModel.locationToAssign
                            location.opState = Enums.OpChangeState.Loaded

                            device.currentLocations.push(location)

                            device.opState = Enums.OpChangeState.Loaded
                        }
                    })
                }
            }

            // #endregion
        }

        // #endregion

        // #region Nowy device z nowym meterem

        if (_.size(locationModel.deviceWithMeter) > 0) {
            locationModel.deviceWithMeter.forEach((model: DeviceMeterModel) => {
                const existingDevice: OpDevice = _.find(toSave.currentDevices, ((dev: OpDevice) => dev.serialNbr == model.serialNbr))

                if (existingDevice != null) {
                    const meter: OpMeter = new OpMeter(null)
                    meter.idMeter = model.idMeter
                    meter.opState = Enums.OpChangeState.Loaded

                    existingDevice.currentMeters.push(meter)
                    existingDevice.opState = Enums.OpChangeState.New

                    const device: OpDevice = new OpDevice(null)
                    device.serialNbr = existingDevice.serialNbr
                    device.opState = Enums.OpChangeState.New

                    meter.currentDevices.push(device)
                    toSave.currentMeters.push(meter)
                } else {
                    const existingMeter: OpMeter = _.find(toSave.currentMeters, (met: OpMeter) => met.idMeter == model.idMeter)

                    if (existingMeter != null) {
                        const device: OpDevice = new OpDevice(null)
                        device.serialNbr = model.serialNbr
                        device.opState = Enums.OpChangeState.Loaded

                        existingMeter.currentDevices.push(device)
                        existingMeter.opState = Enums.OpChangeState.New

                        const meter: OpMeter = new OpMeter(null)
                        meter.idMeter = existingMeter.idMeter
                        meter.opState = Enums.OpChangeState.New

                        device.currentMeters.push(meter)
                        toSave.currentDevices.push(device)
                    }
                }
            })
        }

        // #endregion

        // #region Nowy meter i device jako set

        if (_.size(locationModel.deviceMeter) > 0) {
            locationModel.deviceMeter.forEach((model: DeviceMeterModel) => {
                const existingDevice: OpDevice = _.find(toSave.currentDevices, (device: OpDevice) => device.serialNbr == model.serialNbr)

                if (existingDevice != null) {
                    let meterExist: boolean = true

                    /** current meter on device */
                    let opMeter: OpMeter = _.find(existingDevice.currentMeters, (meter: OpMeter) => meter.idMeter == model.idMeter)

                    /** meter on location */
                    const meterOnLocation: OpMeter = _.find(toSave.currentMeters, (meter: OpMeter) => meter.idMeter == model.idMeter)

                    if (opMeter == null) {
                        meterExist = false

                        if (meterOnLocation != null) {
                            /** meter was replaced */
                            opMeter = meterOnLocation
                        } else {
                            opMeter = new OpMeter(null)
                            opMeter.idMeter = model.idMeter
                        }
                    }

                    opMeter.opState = Enums.OpChangeState.New

                    if (!meterExist) {
                        existingDevice.currentMeters.push(opMeter)
                    }

                    existingDevice.opState = Enums.OpChangeState.New

                    const deviceClone: OpDevice = new OpDevice(null)
                    deviceClone.serialNbr = existingDevice.serialNbr
                    deviceClone.opState = Enums.OpChangeState.New

                    opMeter.currentDevices.push(deviceClone)

                    if (meterOnLocation == null) {
                        toSave.currentMeters.push(opMeter)
                    }
                } else {
                    const existingMeter: OpMeter = _.find(toSave.currentMeters, (meter: OpMeter) => meter.idMeter == model.idMeter)

                    if (existingMeter != null) {
                        let deviceExist: boolean = true

                        let opDevice: OpDevice = _.find(existingMeter.currentDevices, (device: OpDevice) => device.serialNbr == model.serialNbr)
                        if (opDevice == null) {
                            deviceExist = false
                            opDevice = new OpDevice(null)
                        }

                        opDevice.serialNbr = model.serialNbr
                        opDevice.opState = Enums.OpChangeState.New

                        if (!deviceExist) {
                            existingMeter.currentDevices.push(opDevice)
                        }

                        existingMeter.opState = Enums.OpChangeState.New

                        const meterClone: OpMeter = new OpMeter(null)
                        meterClone.idMeter = existingMeter.idMeter
                        meterClone.opState = Enums.OpChangeState.New

                        if (!deviceExist) {
                            opDevice.currentMeters.push(meterClone)
                            toSave.currentDevices.push(opDevice)
                        }
                    }
                }
            })
        }

        // #endregion

        return toSave
    }

    /**
     * This method translates changes from grid into LocationFormModel, and then into OpLocation with location equipment changes.
     * It is in the best interest of the world is to not touch this method until grids compatibile with default business objects are implemented.
     * If you wish to inflict some self-pain and try to refactor this method, append number of wasted hours below.
     * Number of wasted hours: 8
     */
    public saveInstalledDevices(options: InstalledEquipmentLegacyOptions): JQueryPromise<boolean> {
        const deferred: JQueryDeferred<any> = $.Deferred<any>()

        if (options == null) {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

            return deferred.resolve(false).promise()
        }

        // #region Prepare view

        const idMeterColumn: OpViewColumn = options.locationView == null
            ? null
            : options.locationView.getViewColumn(new GetViewColumnOptions(Enums.DataType.HELPER_ID_METER))

        const serialNbrColumn: OpViewColumn = options.deviceView == null
            ? null
            : options.deviceView.getViewColumn(new GetViewColumnOptions(Enums.DataType.HELPER_SERIAL_NBR))

        if (idMeterColumn == null || serialNbrColumn == null) {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

            return deferred.resolve(false).promise()
        }

        const columnNameIdMeter: string = idMeterColumn.name.toLowerCase();
        const columnNameSerialNbr: string = serialNbrColumn.name.toLowerCase()

        // #endregion

        // #region Get device type

        const idDeviceType: number = _.first(
            options.inGrid
                .filter((item: any) => columnNameSerialNbr
                    && item[columnNameSerialNbr] == item[CommonHelper.idField]
                    && item[CommonHelper.idReferenceField] != null)
                .map((item: any) => Number(item[CommonHelper.idReferenceField]).valueOf())
        )

        const deviceType: OpDeviceType = (_.isNumber(idDeviceType) && idDeviceType > 0 && options.deviceTypesDict != null)
            ? options.deviceTypesDict.tryGetValue(idDeviceType) as OpDeviceType
            : null

        const deviceTypeIsSet: boolean = deviceType != null
            && deviceType.dataList != null
            && deviceType.dataList.getValue(Enums.DataType.DEVICE_TYPE_IS_SET, 0, false)

        // #endregion

        // #region Prepare old model before translation to new model and then save it

        const removedDeviceRows: any[] = options.removedRows
        const formModel: LocationFormValuesModel = new LocationFormValuesModel()

        const addedSerialNbr: number[] = options.addedRows.map((gridRow: any) => gridRow[CommonHelper.idField])
        const removedSerialNbr: number[] = options.removedRows.map((gridRow: any) => gridRow[CommonHelper.idField])

        formModel.id = options.location.idLocation
        formModel.devices = options.location.currentDevices.map((device: OpDevice) => device.serialNbr).filter(serial => removedSerialNbr.indexOf(serial) == -1)
        formModel.addedDevices = addedSerialNbr
        formModel.removedDevices = removedSerialNbr

        formModel.deviceMeter = []
        formModel.deviceWithMeter = []

        const isEditedField: string = 'isEdited'
        const isNewField: string = 'isNew'

        options.inGrid.forEach((item: any) => {
            if (item[isEditedField] == true) {
                formModel.deviceMeter.push({ idMeter: item[columnNameIdMeter], serialNbr: item.id })
            } else if (item[isNewField] == true && item[columnNameIdMeter] != null) {
                formModel.deviceWithMeter.push({ idMeter: item[columnNameIdMeter], serialNbr: item.id })
            }
        })

        formModel.addedMeters = []
        formModel.removedMeters = []

        formModel.meters = options.location
            .currentMeters
            .filter((meter: OpMeter) => formModel.deviceWithMeter.every((devMet: DeviceMeterModel) => devMet.idMeter !== meter.idMeter))
            .map((meter: OpMeter) => meter.idMeter)

        if (removedSerialNbr.length > 0) {
            let metersToRemove: number[] = []

            removedDeviceRows.forEach((remDev: any) => {
                options.location.currentMeters.forEach((meter: OpMeter) => {
                    if (remDev[columnNameIdMeter] == meter.idMeter) {
                        metersToRemove.push(meter.idMeter)
                    }
                })
            })

            let isSubmited: boolean = false

            const depositorySelectionPopoverOptions: SourcePopover = {
                popupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectDepositoryToAssign),

                onSubmit: (idLocation: number) => {
                    isSubmited = true

                    formModel.meters = options.location.currentMeters.map((meter: OpMeter) => { return meter.idMeter }).filter((idMeter: number) => metersToRemove.indexOf(idMeter) == -1)
                    formModel.locationToAssign = idLocation

                    const location: OpLocation = this.translateLocationModelToLocationEquipmentChanges(formModel)
                    options.saveLocationDelegate(location)
                        .then((location: OpLocation) => {
                            deferred.resolve(location)
                            return options.locationSavedDelegate(location);
                        })
                        .fail((e: any) => {
                            deferred.reject()
                        })
                        .always(() => {
                            options.saveAlwaysDelegate()
                            isSubmited = false
                        })
                },

                onCancel: () => { },

                onHiding: () => {
                    if (!isSubmited) {
                        deferred
                            .resolve()
                            .promise()
                            .then(() => {
                                return options.saveFailedDelegate()
                            })
                            .always(() => {
                                options.saveAlwaysDelegate()
                            })
                    }
                },

                selectPopoverSource: {
                    dataTypeId: Enums.DataType.DEPOSITORY_ID,
                    dictionaryMethod: 'getLocationSelectColumns',
                    referenceTypeId: Enums.ReferenceType.IdLocation,
                    selectedId: null,
                    dataIdSourceModule: Enums.WebSimaxModule.Depositories,
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
                },

                nothingSelectedMsg: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DepositoryNothingSelected)
            }

            const moveWithMeterConfirmed = () => {
                formModel.meters = options.location.currentMeters.map((meter: OpMeter) => { return meter.idMeter }).filter((idMeter: number) => metersToRemove.indexOf(idMeter) == -1)
                formModel.removedMeters = metersToRemove
                formModel.removeWithMeters = true
                formModel.addedMeters = []
            }

            options.setConfirmationSourceDelegates(
                () => moveWithMeterConfirmed(),
                () => {
                    metersToRemove = []
                    formModel.removeWithMeters = false
                    formModel.removedMeters = []
                }
            )

            options.scope.$broadcast(EventsNames.OPEN_SELECT_POPOVER_POPUP + options.editId, depositorySelectionPopoverOptions)

            if (metersToRemove.length > 0) {
                if (deviceTypeIsSet) {
                    moveWithMeterConfirmed()
                } else {
                    this.$timeout(() => {
                        options.scope.$broadcast(
                            EventsNames.OPEN_CONFIRMATION_POPUP + options.editId,
                            LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RemoveMeterToo)
                        )
                    })
                }
            } else {
                formModel.removeWithMeters = false
            }
        } else {
            const location: OpLocation = this.translateLocationModelToLocationEquipmentChanges(formModel)
            return options.saveLocationDelegate(location)
                .then((location: OpLocation) => {
                    deferred.resolve(location)
                    return options.locationSavedDelegate(location)
                })
                .fail((e: any) => {
                    deferred.reject()
                })
                .always(() => {
                    options.saveAlwaysDelegate()
                })
        }

        return deferred.promise()

        // #endregion
    }

    /**
     * This method translates changes from grid into LocationFormModel, and then into OpLocation with location equipment changes.
     * It is in the best interest of the world is to not touch this method until grids compatibile with default business objects are implemented.
     * If you wish to inflict some self-pain and try to refactor this method, append number of wasted hours below.
     * Number of wasted hours: 6
     */
    public saveInstalledMeters(options: InstalledEquipmentLegacyOptions): JQueryPromise<boolean> {
        const deferred: JQueryDeferred<any> = $.Deferred<any>()

        if (options == null) {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

            return deferred.resolve(false).promise()
        }

        // #region Prepare view

        const idMeterColumn: OpViewColumn = options.locationView == null
            ? null
            : options.locationView.getViewColumn(new GetViewColumnOptions(Enums.DataType.HELPER_ID_METER))

        if (idMeterColumn == null) {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

            return deferred.resolve(false).promise()
        }

        const columnNameIdMeter: string = idMeterColumn.name.toLowerCase();

        const serialNbrColumn: OpViewColumn = options.deviceView == null
            ? null
            : options.deviceView.getViewColumn(new GetViewColumnOptions(Enums.DataType.HELPER_SERIAL_NBR))

        if (serialNbrColumn == null) {
            ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))

            return deferred.resolve(false).promise()
        }

        const columnNameSerialNbr: string = serialNbrColumn.name.toLowerCase();

        // #endregion

        const removedMeters: any[] = options.removedRows
        const formModel: LocationFormValuesModel = new LocationFormValuesModel()

        const added: number[] = options.addedRows.map((gridRow: any) => gridRow[CommonHelper.idField])
        const removed: number[] = options.removedRows.map((gridRow: any) => gridRow[CommonHelper.idField])

        formModel.id = options.location.idLocation
        formModel.meters = options.location.currentMeters.map((meter: OpMeter) => meter.idMeter).filter((idMeter: number) => removed.indexOf(idMeter) == -1)
        formModel.addedMeters = added
        formModel.removedMeters = removed

        formModel.deviceMeter = []
        formModel.deviceWithMeter = []

        const isEditedField: string = 'isEdited'
        const isNewField: string = 'isNew'

        options.inGrid.forEach((gridRow: any) => {
            if (gridRow[isEditedField] == true) {
                formModel.deviceMeter.push({ serialNbr: gridRow[columnNameSerialNbr], idMeter: gridRow.id })
            } else if (gridRow[isNewField] == true && gridRow[columnNameSerialNbr] != null) {
                formModel.deviceWithMeter.push({ idMeter: gridRow[columnNameIdMeter], serialNbr: gridRow[columnNameSerialNbr] })
            }
        })

        formModel.addedDevices = []
        formModel.removedDevices = []

        formModel.devices = options.location
            .currentDevices
            .filter((device: OpDevice) => formModel.deviceWithMeter.every((devMet: DeviceMeterModel) => devMet.serialNbr !== device.serialNbr))
            .map((device: OpDevice) => device.serialNbr)

        if (removed.length > 0) {
            let devicesToRemove: number[] = []

            removedMeters.forEach((remMet: any) => {
                options.location.currentDevices.forEach((device: OpDevice) => {
                    if (remMet[columnNameSerialNbr] == device.serialNbr)
                        devicesToRemove.push(device.serialNbr)
                })
            })

            let isSubmited: boolean = false

            const depositorySelectionPopoverOptions: SourcePopover = {
                popupTitle: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.SelectDepositoryToAssign),

                onSubmit: (id: number) => {
                    isSubmited = true
                    formModel.devices = options.location.currentDevices.map((device: OpDevice) => { return device.serialNbr })
                    formModel.locationToAssign = id

                    const location: OpLocation = this.translateLocationModelToLocationEquipmentChanges(formModel)
                    return options.saveLocationDelegate(location)
                        .then((location: OpLocation) => {
                            deferred.resolve(location)
                            return options.locationSavedDelegate(location)
                        })
                        .fail((e: any) => {
                            deferred.reject()
                        })
                        .always(() => {
                            options.saveAlwaysDelegate()
                            isSubmited = false
                        })
                },

                onCancel: () => { },

                onHiding: () => {
                    if (!isSubmited) {
                        deferred
                            .resolve()
                            .promise()
                            .then(() => {
                                return options.saveFailedDelegate()
                            })
                            .always(() => {
                                options.saveAlwaysDelegate()
                            })
                    }
                },

                selectPopoverSource: {
                    dataTypeId: Enums.DataType.DEPOSITORY_ID,
                    dictionaryMethod: 'getLocationSelectColumns',
                    referenceTypeId: Enums.ReferenceType.IdLocation,
                    selectedId: null,
                    dataIdSourceModule: Enums.WebSimaxModule.Depositories,
                    groupReferenceType: Enums.ReferenceType.IdDistributor,
                    groupReferenceValue: this.userSession.getLoggedOperator().idDistributor
                },

                nothingSelectedMsg: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DepositoryNothingSelected)
            }

            options.setConfirmationSourceDelegates(
                () => {
                    formModel.removedDevices = devicesToRemove
                    formModel.removeWithDevices = true
                    formModel.addedDevices = []
                },
                () => {
                    devicesToRemove = []
                    formModel.removeWithDevices = false
                    formModel.removedDevices = []
                }
            )

            options.scope.$broadcast(EventsNames.OPEN_SELECT_POPOVER_POPUP + options.editId, depositorySelectionPopoverOptions)

            if (devicesToRemove.length > 0) {
                this.$timeout(() => {
                    options.scope.$broadcast(
                        EventsNames.OPEN_CONFIRMATION_POPUP + options.editId,
                        LocalizationHelper.getLocalizedText(LocalizationHelper.Text.RemoveDeviceToo)
                    )
                })
            } else {
                formModel.removeWithDevices = false
            }
        } else {
            const location: OpLocation = this.translateLocationModelToLocationEquipmentChanges(formModel)
            return options.saveLocationDelegate(location)
                .then((location: OpLocation) => {
                    deferred.resolve(location)
                    return options.locationSavedDelegate(location)
                })
                .fail((e: any) => {
                    deferred.reject()
                })
                .always(() => {
                    options.saveAlwaysDelegate()
                })
        }

        return deferred.promise()
    }

    // #endregion

    // #region Private Methods

    // #endregion
}

// #endregion

export class LocationEquipmentServiceProvider
    implements ng.IServiceProvider {
    // #region IServiceProvider Implementation 

    public $get = [
        $TIMEOUT_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            $timeout: ng.ITimeoutService,
            userSession: UserSessionService
        ) {
            return new LocationEquipmentService($timeout, userSession)
        }
    ]

    // #endregion
} 
